/** The decision that changed a farmer's points, not each point movement it caused. */
export enum PointsAuditEventType {
  AccountsLinked = 'accounts-linked',
  AccountsUnlinked = 'accounts-unlinked',
  /** An energy behavior marked completed, by hand or by a customer behaviors CSV row. */
  BehaviorCompleted = 'behavior-completed',
  BehaviorReversed = 'behavior-reversed',
  /** From the process-expired-bookings cron. */
  BookingPointsReturned = 'booking-points-returned',
  /** The ERP categories mapped to a program's categories. */
  CategoryMappingChanged = 'category-mapping-changed',
  ExclusionChanged = 'exclusion-changed',
  ImportCompleted = 'import-completed',
  ImportDiscarded = 'import-discarded',
  ManualPointsAdjusted = 'manual-points-adjusted',
  /** The amount, order or product of a hand-issued ledger row changed. */
  ManualPointsEdited = 'manual-points-edited',
  /** A customer joined or left the agronomy or energy program. */
  MembershipChanged = 'membership-changed',
  /** A program's points expiration date moved, and with it the expiry of points already issued. */
  PointsExpirationChanged = 'points-expiration-changed',
  /** One row of an 'update points' CSV. */
  PointsImported = 'points-imported',
  ProductExclusionChanged = 'product-exclusion-changed',
  /** An agronomy program's dates, tiers, earning rules or spend exclusions. */
  ProgramSettingsChanged = 'program-settings-changed',
  PromotionCreated = 'promotion-created',
  PromotionDeleted = 'promotion-deleted',
  PromotionEdited = 'promotion-edited',
  RecalculationQueued = 'recalculation-queued',
  Revert = 'revert',
  SyncCompleted = 'sync-completed',
  TierOverrideSet = 'tier-override-set',
}

/** Why every energy member's orders were re-priced, recorded on a RecalculationQueued row. */
export enum EnergyRequeueReason {
  CategoryMappingChanged = 'category-mapping-changed',
  MarkedReady = 'marked-ready',
  ProgramDatesChanged = 'program-dates-changed',
  TierRulesChanged = 'tier-rules-changed',
}

export enum PointsAuditActorType {
  Cron = 'cron',
  Import = 'import',
  Integration = 'integration',
  System = 'system',
  User = 'user',
}

export enum PointsAuditProcessType {
  Import = 'import',
  Manual = 'manual',
  Sync = 'sync',
}

export enum PointsAuditScope {
  Farmer = 'farmer',
  Retailer = 'retailer',
}

/** How a row is undone. Only human decisions that can't be recomputed carry a before-image. */
export enum PointsAuditRevertStrategy {
  /** A reversing movement: hand-issued points survive a recompute. */
  Compensate = 'compensate',
  None = 'none',
  Recompute = 'recompute',
  Restore = 'restore',
}

/**
 * Why a point movement happened, derived from the PointsHistory row. No Tier value: a tier changes
 * the earning rate and writes no movements of its own.
 */
export enum PointsMovementReason {
  BaseEarning = 'base-earning',
  /** Points moved into or out of a booking's reserve, not earned or spent. */
  BookingReserve = 'booking-reserve',
  Correction = 'correction',
  Expiration = 'expiration',
  /** The manual adjustment screen or an 'update points' CSV row. */
  Manual = 'manual',
  Promotion = 'promotion',
  Redemption = 'redemption',
  Refund = 'refund',
  Return = 'return',
}

export const REVERT_STRATEGY_BY_EVENT_TYPE: Record<
  PointsAuditEventType,
  PointsAuditRevertStrategy
> = {
  [PointsAuditEventType.AccountsLinked]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.AccountsUnlinked]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.BehaviorCompleted]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.BehaviorReversed]: PointsAuditRevertStrategy.Restore,
  // The booking stays closed, so the next expired-bookings run would return the points again.
  [PointsAuditEventType.BookingPointsReturned]: PointsAuditRevertStrategy.None,
  // Settings are changed back on their own screens; the log records them so they hold order.
  [PointsAuditEventType.CategoryMappingChanged]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.ExclusionChanged]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.ImportCompleted]: PointsAuditRevertStrategy.Recompute,
  [PointsAuditEventType.ImportDiscarded]: PointsAuditRevertStrategy.None,
  // Manually issued, so a recompute leaves them standing.
  [PointsAuditEventType.ManualPointsAdjusted]: PointsAuditRevertStrategy.Compensate,
  [PointsAuditEventType.ManualPointsEdited]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.MembershipChanged]: PointsAuditRevertStrategy.Restore,
  // The change moves every balance in the program to one date, so their own dates can't come back.
  [PointsAuditEventType.PointsExpirationChanged]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.PointsImported]: PointsAuditRevertStrategy.Compensate,
  [PointsAuditEventType.ProductExclusionChanged]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.ProgramSettingsChanged]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.PromotionCreated]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.PromotionDeleted]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.PromotionEdited]: PointsAuditRevertStrategy.Restore,
  [PointsAuditEventType.RecalculationQueued]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.Revert]: PointsAuditRevertStrategy.None,
  // Agvance owns its data: a bad sync is fixed there and sent again, never reverted here.
  [PointsAuditEventType.SyncCompleted]: PointsAuditRevertStrategy.None,
  [PointsAuditEventType.TierOverrideSet]: PointsAuditRevertStrategy.Restore,
};

/** The row an import or sync writes once, carrying its counts: what the whole run did. */
export const RUN_SUMMARY_EVENT_TYPES: PointsAuditEventType[] = [
  PointsAuditEventType.ImportCompleted,
  PointsAuditEventType.SyncCompleted,
];

/**
 * The events a revert can actually undo. Restore rows count only once their event has a restore;
 * the rest come back as conflicts, so a process made of them has nothing to revert.
 */
export const UNDOABLE_EVENT_TYPES: PointsAuditEventType[] = [
  PointsAuditEventType.BehaviorCompleted,
  PointsAuditEventType.BehaviorReversed,
  PointsAuditEventType.ExclusionChanged,
  PointsAuditEventType.ImportCompleted,
  PointsAuditEventType.ManualPointsAdjusted,
  PointsAuditEventType.MembershipChanged,
  PointsAuditEventType.PointsImported,
  PointsAuditEventType.TierOverrideSet,
];

/** Rows that change nothing, so they never stand in the way of reverting an older change. */
export const ORDER_NEUTRAL_EVENT_TYPES: PointsAuditEventType[] = [
  PointsAuditEventType.ImportDiscarded,
  PointsAuditEventType.Revert,
];

/** Why a process that a revert could otherwise undo can't be reverted right now. */
export enum PointsAuditRevertBlock {
  /** Logged before scopes were recorded, so a revert can't tell who it would reach. */
  Locked = 'locked',
  /** A newer change at the retailer still stands; it has to be reverted first. */
  NewerChange = 'newer-change',
  /** Points are being recalculated at the retailer, or an import or sync is still processing. */
  Recalculating = 'recalculating',
}
